import { ExclamationIcon } from '@heroicons/react/solid'
import { useWeb3React } from '@web3-react/core'
import { Box, SkeletonCircle, SkeletonRectangle, Text } from 'components'
import { SectionContainer } from 'components/SectionContainer'
import { configConstants } from 'config'
import { ColorType } from 'constants/colors'
import { useModal } from 'contexts'
import { BigNumber, utils } from 'ethers'
import { useRef } from 'react'
import styled from 'styled-components'
import { formatBigNumber, getIconUrlFromGCP, getSpacing } from 'utils'
import { CoreCapsuleWithAccount } from '../../../entities'
import { VaultModal } from '../VaultModal'

interface IVaultCardMobileProps {
  coreCapsule?: CoreCapsuleWithAccount
}

export const VaultCardMobile = ({ coreCapsule }: IVaultCardMobileProps) => {
  const { openWith } = useModal()
  const { chainId } = useWeb3React()
  const fomatter = useRef(
    Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }),
  )

  const limitScoreAttributes = coreCapsule?.creditLimitAttributes
  const scoreThreshold = limitScoreAttributes?.threshold || '0'
  const coreMaxLTV: BigNumber = coreCapsule?.maxLTV
  const interestRate: BigNumber = coreCapsule?.yearlyInterestRate
  const tvl: BigNumber = coreCapsule?.totalValueLocked
  const borrowUsage: BigNumber = coreCapsule?.accountProperties?.borrowUsage || BigNumber.from(0)
  const isLocked = !coreCapsule?.accountCanView
  const valueColor = isLocked ? 'WHITE_50' : 'WHITE'
  const iconName = isLocked ? 'locked' : coreCapsule?.collateralSymbol.toLowerCase()
  const maxLtvRounded = coreMaxLTV && Number(formatBigNumber(coreMaxLTV.mul(100), 2)).toFixed(0)
  let usageColor: ColorType = isLocked ? 'WHITE_50' : 'WHITE'
  let icon: JSX.Element

  if (borrowUsage.gt(configConstants.atRiskThreshold)) {
    usageColor = 'RED_5'
    icon = <ExclamationIcon width={'24px'} fill={'ORANGE'} />
  } else if (borrowUsage.gt(0)) {
    usageColor = 'GREEN_5'
  }

  const isOnUnsupportedChain = ![1, 137, 80001, 5].includes(chainId)

  if (isOnUnsupportedChain) {
    return (
      <SectionContainer marginTop={getSpacing('m')}>
        <Text center weight="regular" size="s">
          Unsupported network. <br /> Please switch to Polygon to start borrowing.
        </Text>
      </SectionContainer>
    )
  } else {
    return (
      <SectionContainer
        marginTop={getSpacing('m')}
        onClick={() => coreCapsule && openWith(<VaultModal coreCapsule={coreCapsule} />)}
      >
        <Box flexDirection="row" alignItems="center">
          {coreCapsule ? (
            <img src={getIconUrlFromGCP(iconName)} width="40px" />
          ) : (
            <SkeletonCircle />
          )}
          <Box marginLeft={getSpacing('m')}>
            {limitScoreAttributes?.title ?? <SkeletonRectangle width="70px" />}
          </Box>
        </Box>
        <Separator />
        <CardLine
          description="Score threshold"
          value={`${scoreThreshold}+`}
          valueColor={valueColor}
        />
        <CardLine
          description="Capital efficiency"
          value={coreMaxLTV && `Up to ${maxLtvRounded}% LTV`}
          valueColor={valueColor}
        />
        <CardLine
          description="Total value locked"
          value={tvl && `$${fomatter.current.format(parseFloat(utils.formatEther(tvl)))}`}
          valueColor={valueColor}
        />
        <CardLine
          description="Interest rate"
          value={interestRate && `${formatBigNumber(interestRate.mul(100))}%`}
          valueColor={valueColor}
        />
        <CardLine
          description="Borrow usage"
          value={
            borrowUsage !== undefined && borrowUsage.gt(0)
              ? `${formatBigNumber(borrowUsage.mul(100), 2)}%`
              : '-'
          }
          valueColor={usageColor}
          icon={icon}
        />
      </SectionContainer>
    )
  }
}

interface ICardItemProps {
  description: string
  valueColor?: ColorType
  value?: string
  icon?: React.ReactNode
}

const CardLine = ({ value, valueColor, description, icon }: ICardItemProps) => {
  return (
    <Box
      justifyContent="space-between"
      alignItems="center"
      flexDirection="row"
      width="100%"
      marginTop={getSpacing('xs')}
    >
      <Text size="s" weight="regular" color="TEXT_DARK">
        {description}
      </Text>
      <Box flexDirection="row" alignItems="center">
        <Box marginRight={getSpacing('xs')}> {icon}</Box>
        {value ? (
          <Text size="s" weight="regular" color={valueColor ?? 'TEXT_LIGHT'}>
            {value}
          </Text>
        ) : (
          <SkeletonRectangle width="70px" />
        )}
      </Box>
    </Box>
  )
}

const Separator = styled.div`
  height: 1px;
  background-color: #ffffff;
  opacity: 20%;
  width: 100%;
  margin-top: ${getSpacing('s')};
  margin-bottom: ${getSpacing('xs')};
`
